// WP-0.7 (§18.1 layout frame): responsive left sidebar. Three bands (§18.1):
//   mobile <640  → hidden (the bottom nav + Menu sheet cover navigation);
//   tablet 640–1024 → 72px icon rail (labels hidden);
//   desktop ≥1024 → 264px full sidebar, collapsible to the 72px rail via the top-bar toggle.
// Tailwind defaults sm=640, lg=1024 map exactly onto the spec bands.
import { NavLink } from 'react-router-dom';
import { useInboxBadgeCount } from '../lib/inbox';
import { NAV_SECTIONS } from '../routes/nav';
import { Icon } from './Icon';
import { Mark } from './Mark';

export function Sidebar({ collapsed }: { collapsed: boolean }): React.JSX.Element {
  // IN-5: badge counts everything actionable (not yet auto-recorded) in the Inbox.
  const inboxBadge = useInboxBadgeCount();
  // Labels: never on the rail (tablet or collapsed desktop); on at lg when expanded.
  const labelClass = collapsed ? 'hidden' : 'hidden lg:inline';
  const headerClass = collapsed ? 'hidden' : 'hidden lg:block';
  const width = collapsed ? 'w-[72px]' : 'w-[72px] lg:w-[264px]';

  return (
    <nav
      aria-label="Primary"
      className={`hidden shrink-0 overflow-y-auto border-r border-border bg-surface transition-[width] sm:block ${width}`}
    >
      <div className="flex h-14 items-center gap-2 border-b border-border px-4">
        <Mark size={22} />
        <span className={`text-sm font-semibold tracking-wide text-ink ${headerClass}`}>PF-OS</span>
      </div>
      <div className="py-3">
        {NAV_SECTIONS.map((section) => (
          <div key={section.group} className="mb-2">
            {section.label !== null && (
              <p
                className={`px-4 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-muted ${headerClass}`}
              >
                {section.label}
              </p>
            )}
            {section.items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                title={item.label}
                className={({ isActive }) =>
                  `mx-2 flex items-center gap-3 rounded-[var(--radius-control)] border-l-2 py-2 pl-[10px] pr-3 text-sm ${
                    isActive
                      ? 'border-primary bg-primary/10 font-medium text-primary'
                      : 'border-transparent text-ink hover:bg-bg'
                  }`
                }
              >
                <Icon name={item.icon} />
                <span className={labelClass}>{item.label}</span>
                {item.path === '/inbox' && inboxBadge > 0 && (
                  <span
                    className={`ml-auto rounded-full bg-danger px-1.5 py-0.5 text-[10px] font-medium text-white ${labelClass}`}
                  >
                    {inboxBadge}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </div>
    </nav>
  );
}
